import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { clearIntent, intentKey } from '@/api/idempotency';
import type {
  AcceptResult,
  CalculatePreview,
  CommercialDocument,
  PricingInput,
  Quotation,
  QuoteStatus,
  UUID,
} from '@/api/types';
import type { Decimal } from '@/lib/format/decimal';
import type { Currency } from '@/lib/format/money';

export const quoteKeys = {
  all: ['quotes'] as const,
  lists: ['quotes', 'list'] as const,
  list: (f: { status?: QuoteStatus | null; q?: string; customerId?: UUID | null; hideDiscarded?: boolean }) => ['quotes', 'list', f] as const,
  detail: (id: UUID) => ['quotes', 'detail', id] as const,
  documents: (id: UUID) => ['quotes', 'documents', id] as const,
  costs: (id: UUID) => ['quotes', 'costs', id] as const,
};

export const quotesQuery = (
  f: { status?: QuoteStatus | null; q?: string; customerId?: UUID | null; hideDiscarded?: boolean },
  size = 50,
) =>
  queryOptions({
    queryKey: quoteKeys.list(f),
    queryFn: ({ signal }) =>
      api<Quotation[]>('/quotations', {
        query: { status: f.status ?? undefined, q: f.q, customerId: f.customerId ?? undefined, hideDiscarded: f.hideDiscarded || undefined, size },
        signal,
      }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

export const quoteQuery = (id: UUID | undefined) =>
  queryOptions({
    queryKey: quoteKeys.detail(id ?? ''),
    queryFn: ({ signal }) => api<Quotation>(`/quotations/${id}`, { signal }),
    enabled: !!id,
  });

export const quoteDocumentsQuery = (id: UUID | undefined, poll = false) =>
  queryOptions({
    queryKey: quoteKeys.documents(id ?? ''),
    queryFn: ({ signal }) => api<CommercialDocument[]>('/documents', { query: { quotationId: id }, signal }),
    enabled: !!id,
    refetchInterval: (q) => {
      if (!poll) return false;
      const docs = q.state.data ?? [];
      const busy = docs.some((d) => d.status === 'PENDING' || d.status === 'PROCESSING');
      return busy ? Math.min(1000 * 2 ** Math.min(q.state.dataUpdateCount, 3), 5000) : false;
    },
  });

export interface EstimatedCost {
  id: UUID;
  description: string;
  category: string;
  amount: Decimal;
  currency: Currency;
  contractAmount: Decimal | null;
}

export const estimatedCostsQuery = (id: UUID | undefined, enabled: boolean) =>
  queryOptions({
    queryKey: quoteKeys.costs(id ?? ''),
    queryFn: ({ signal }) => api<EstimatedCost[]>(`/quotations/${id}/estimated-costs`, { signal }),
    enabled: !!id && enabled,
  });

export interface QuoteDefaults {
  validityDays: number;
  validUntil: string;
  depositPercent: Decimal;
  defaultOwnerId: UUID | null;
  conditions: string[];
}

export const quoteDefaultsQuery = () =>
  queryOptions({
    queryKey: ['quotes', 'defaults'],
    queryFn: ({ signal }) => api<QuoteDefaults>('/quotations/defaults', { signal }),
    staleTime: 5 * 60_000,
  });

export function calculate(
  body: { siteId: UUID | null; assignedTo?: UUID | null; pricing: PricingInput },
  signal?: AbortSignal,
): Promise<CalculatePreview> {
  return api<CalculatePreview>('/quotations/calculate', { body, signal });
}

function useQuoteWrite<TVars>(fn: (vars: TVars) => Promise<Quotation>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (quote) => {
      client.setQueryData(quoteKeys.detail(quote.id), quote);
      void client.invalidateQueries({ queryKey: quoteKeys.lists });
    },
  });
}

export interface CreateQuote {
  customerId: UUID;
  siteId: UUID;
  ownerId: UUID;
  assignedTo: UUID | null;
  pricing: PricingInput;
}

export function useCreateQuote() {
  return useQuoteWrite((input: CreateQuote & { draftId: string }) => {
    const { draftId, ...body } = input;
    // Keyed by the local draft: a double click or a retry after a network error creates one quote, not two.
    return api<Quotation>('/quotations', { body, idempotencyKey: intentKey(`quote-create:${draftId}`, draftId) });
  });
}

export function useSaveDraft() {
  return useQuoteWrite(({ id, version, pricing }: { id: UUID; version: number; pricing: PricingInput }) =>
    api<Quotation>(`/quotations/${id}/draft`, { method: 'PUT', body: { version, pricing } }),
  );
}

export function useNewRevision() {
  return useQuoteWrite(({ id, pricing }: { id: UUID; pricing: PricingInput }) =>
    api<Quotation>(`/quotations/${id}/revisions`, { body: pricing }),
  );
}

export function useIssue() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, revisionId, version }: { id: UUID; revisionId: UUID; version: number }) => {
      const scope = `quote-issue:${id}:${revisionId}`;
      const body = { revisionId, version };
      const quote = await api<Quotation>(`/quotations/${id}/issue`, { body, idempotencyKey: intentKey(scope, body) });
      clearIntent(scope);
      return quote;
    },
    onSuccess: (quote) => {
      client.setQueryData(quoteKeys.detail(quote.id), quote);
      void client.invalidateQueries({ queryKey: quoteKeys.lists });
      void client.invalidateQueries({ queryKey: quoteKeys.documents(quote.id) });
    },
  });
}

export function useAccept() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, revisionId, evidence }: { id: UUID; revisionId: UUID; evidence: string }) => {
      const scope = `quote-accept:${id}:${revisionId}`;
      const body = { revisionId, evidence };
      const result = await api<AcceptResult>(`/quotations/${id}/accept`, { body, idempotencyKey: intentKey(scope, body) });
      clearIntent(scope);
      return result;
    },
    onSuccess: (result) => {
      client.setQueryData(quoteKeys.detail(result.quotation.id), result.quotation);
      void client.invalidateQueries({ queryKey: quoteKeys.lists });
      void client.invalidateQueries({ queryKey: ['works'] });
    },
  });
}

export function useDiscard() {
  return useQuoteWrite(({ id, reason }: { id: UUID; reason: string }) =>
    api<Quotation>(`/quotations/${id}/discard`, { body: { reason } }),
  );
}

/** Removes a quote that never left draft (a test, a mistake). Anything issued is discarded instead. */
export function useDeleteQuote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => api<void>(`/quotations/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      client.removeQueries({ queryKey: quoteKeys.detail(id) });
      void client.invalidateQueries({ queryKey: quoteKeys.lists });
    },
  });
}

export function useRejectRevision() {
  return useQuoteWrite(({ id, revisionId, reason }: { id: UUID; revisionId: UUID; reason: string }) =>
    api<Quotation>(`/quotations/${id}/reject-revision`, { body: { revisionId, reason } }),
  );
}

export function useReplaceEstimatedCosts(id: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (costs: Array<{ description: string; category: string; amount: Decimal; currency: Currency }>) =>
      api<EstimatedCost[]>(`/quotations/${id}/estimated-costs`, { method: 'PUT', body: { costs } }),
    onSuccess: (costs) => client.setQueryData(quoteKeys.costs(id), costs),
  });
}

export function useRetryDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => api<CommercialDocument>(`/documents/${id}/retry`, { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['quotes', 'documents'] }),
  });
}
